import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  type DocumentData,
  type Query as FirestoreQuery,
} from "firebase/firestore";
import { documentId } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import type { ManufacturerRep } from "../types/DB2";
import { useUserData } from "./UserDataFetcher";

type ManufacturerRepsCtx = {
  reps: ManufacturerRep[];
  refresh: () => Promise<void>;
  createRep: (payload: Partial<ManufacturerRep>) => Promise<string>;
  updateRep: (id: string, payload: Partial<ManufacturerRep>) => Promise<void>;
  deleteRep: (id: string) => Promise<void>;
};

const ManufacturerRepsContext = createContext<ManufacturerRepsCtx | undefined>(undefined);

const sanitizeId = (raw: string) =>
  raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || `rep-${Math.random().toString(36).slice(2, 10)}`;

const normalizeArray = (value?: unknown): string[] | undefined => {
  if (!value) return undefined;
  if (Array.isArray(value)) {
    const next = value
      .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
      .filter(Boolean);
    return next.length ? Array.from(new Set(next)) : undefined;
  }
  return undefined;
};

const logTag = "[ManufacturerRepsDataProvider]";

export const ManufacturerRepsDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [reps, setReps] = useState<ManufacturerRep[]>([]);
  const db = getFirestore();
  const auth = getAuth();
  const userData = useUserData();
  const manufacturerCandidates = useMemo(() => {
    const ids = new Set<string>();
    if (typeof userData?.manufacturerId === "string" && userData.manufacturerId.trim()) {
      ids.add(userData.manufacturerId.trim());
    }
    const extra = (userData as any)?.manufacturerIds;
    if (Array.isArray(extra)) {
      extra.forEach((value) => {
        if (typeof value === "string" && value.trim()) {
          ids.add(value.trim());
        }
      });
    }
    return Array.from(ids);
  }, [userData?.manufacturerId, (userData as any)?.manufacturerIds]);

  const manufacturerId = manufacturerCandidates[0] || null;
  const isDeveloper = userData?.role === "developer" || userData?.isDev === true;
  const repDocId = typeof userData?.manufacturerRepId === "string" && userData.manufacturerRepId.trim()
    ? userData.manufacturerRepId.trim()
    : null;

  const fetchReps = useCallback(async () => {
    try {
      console.debug(`${logTag} fetchReps:start`, {
        isDeveloper,
        manufacturerId,
        repDocId,
        userRole: userData?.role,
        hasUserData: !!userData,
      });
      const colRef = collection(db, "manufacturerReps");
      let repQuery: FirestoreQuery<DocumentData> = colRef;
      if (!isDeveloper) {
        if (manufacturerId) {
          repQuery = query(colRef, where("manufacturerIds", "array-contains", manufacturerId));
          console.debug(`${logTag} fetchReps:usingManufacturerFilter`, { manufacturerId });
        } else if (userData?.role === "manufacturerRep" && repDocId) {
          repQuery = query(colRef, where(documentId(), "==", repDocId));
          console.debug(`${logTag} fetchReps:usingRepFilter`, { repDocId });
        } else {
          console.warn(`${logTag} fetchReps:noFilterAvailable`, { userRole: userData?.role });
          setReps([]);
          return;
        }
      }
      const snap = await getDocs(repQuery);
      const list: ManufacturerRep[] = [];
      snap.forEach((docSnap) => {
        list.push({ id: docSnap.id, ...(docSnap.data() as any) } as ManufacturerRep);
      });
      console.debug(`${logTag} fetchReps:succeeded`, { count: list.length });
      setReps(list);
    } catch (error) {
      console.error(`${logTag} failed to fetch reps`, {
        error,
        isDeveloper,
        manufacturerId,
        repDocId,
        userRole: userData?.role,
      });
      setReps([]);
    }
  }, [db, isDeveloper, manufacturerId, repDocId, userData?.role]);

  useEffect(() => {
    fetchReps();
  }, [fetchReps]);

  const createRep = async (payload: Partial<ManufacturerRep>): Promise<string> => {
    const name = String(payload.name || "").trim();
    const email = String(payload.contactInfo?.email || "").trim();
    if (!name) throw new Error("Representative name is required");
    if (!email) throw new Error("Primary contact email is required");
    const manufacturerIds = normalizeArray(payload.manufacturerIds);
    if (!manufacturerIds || !manufacturerIds.length) {
      throw new Error("At least one manufacturerId is required");
    }

    const docId = sanitizeId(name);
    const ref = doc(db, "manufacturerReps", docId);
    const now = serverTimestamp();
    const createdBy = auth.currentUser?.uid || "unknown";
    const phone = payload.contactInfo?.phone ? String(payload.contactInfo.phone).trim() : undefined;
    const address = payload.contactInfo?.address ? String(payload.contactInfo.address).trim() : undefined;
    const data: Record<string, any> = {
      ...payload,
      name,
      contactInfo: {
        email,
        ...(phone ? { phone } : {}),
        ...(address ? { address } : {}),
      },
      manufacturerIds,
      status: payload.status || "active",
      isDemo: payload.isDemo ?? false,
      createdAt: now,
      createdBy,
    };
    await setDoc(ref, data, { merge: true });
    await fetchReps();
    return docId;
  };

  const updateRep = async (id: string, payload: Partial<ManufacturerRep>) => {
    if (!id) throw new Error("Missing representative id");
    const ref = doc(db, "manufacturerReps", id);
    const manufacturerIds = normalizeArray(payload.manufacturerIds);
    const adminUserIds = normalizeArray(payload.adminUserIds);
    const data: Record<string, any> = {
      ...payload,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || "unknown",
    };
    if (manufacturerIds) data.manufacturerIds = manufacturerIds;
    if (adminUserIds) data.adminUserIds = adminUserIds;
    Object.keys(data).forEach((key) => {
      if (data[key] === undefined) delete data[key];
    });
    await setDoc(ref, data, { merge: true });
    await fetchReps();
  };

  const removeRep = async (id: string) => {
    if (!id) return;
    const ref = doc(db, "manufacturerReps", id);
    await deleteDoc(ref);
    await fetchReps();
  };

  return (
    <ManufacturerRepsContext.Provider value={{ reps, refresh: fetchReps, createRep, updateRep, deleteRep: removeRep }}>
      {children}
    </ManufacturerRepsContext.Provider>
  );
};

export const useManufacturerRepsData = () => {
  const ctx = useContext(ManufacturerRepsContext);
  if (!ctx) throw new Error("useManufacturerRepsData must be used within ManufacturerRepsDataProvider");
  return ctx;
};

export default ManufacturerRepsDataProvider;
